"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Board = void 0;
const SpaceType_1 = require("../SpaceType");
const TileType_1 = require("../TileType");
const AresHandler_1 = require("../ares/AresHandler");
const SpaceBonus_1 = require("../SpaceBonus");
const CardName_1 = require("../CardName");
class Board {
    constructor(spaces) {
        this.spaces = spaces;
        this.maxX = 0;
        this.maxY = 0;
        this.adjacentSpaces = new Map();
        this.maxX = Math.max(...spaces.map((s) => s.x));
        this.maxY = Math.max(...spaces.map((s) => s.y));
        spaces.forEach((space) => {
            this.adjacentSpaces.set(space.id, this.computeAdjacentSpaces(space));
        });
    }
    ;
    getSpace(id) {
        const space = this.spaces.find((space) => space.id === id);
        if (space === undefined) {
            throw new Error(`Can't find space with id ${id}`);
        }
        return space;
    }
    computeAdjacentSpaces(space) {
        const middleRow = this.maxY / 2;
        if (space.spaceType !== SpaceType_1.SpaceType.COLONY) {
            if (space.y < 0 || space.y > this.maxY) {
                throw new Error('Unexpected space y value: ' + space.y);
            }
            if (space.x < 0 || space.x > this.maxX) {
                throw new Error('Unexpected space x value: ' + space.x);
            }
            const leftSpace = [space.x - 1, space.y];
            const rightSpace = [space.x + 1, space.y];
            const topLeftSpace = [space.x, space.y - 1];
            const topRightSpace = [space.x, space.y - 1];
            const bottomLeftSpace = [space.x, space.y + 1];
            const bottomRightSpace = [space.x, space.y + 1];
            if (space.y < middleRow) {
                bottomLeftSpace[0]--;
                topRightSpace[0]++;
            }
            else if (space.y === middleRow) {
                bottomRightSpace[0]++;
                topRightSpace[0]++;
            }
            else {
                bottomRightSpace[0]++;
                topLeftSpace[0]--;
            }
            const coords = [
                topLeftSpace,
                topRightSpace,
                rightSpace,
                bottomRightSpace,
                bottomLeftSpace,
                leftSpace,
            ];
            const spaces = [];
            for (const [x, y] of coords) {
                const adj = this.spaces.find((adj) => space !== adj && adj.spaceType !== SpaceType_1.SpaceType.COLONY &&
                    adj.x === x && adj.y === y);
                if (adj !== undefined) {
                    spaces.push(adj);
                }
            }
            return spaces;
        }
        return [];
    }
    getAdjacentSpaces(space) {
        const spaces = this.adjacentSpaces.get(space.id);
        if (spaces === undefined) {
            throw new Error(`Unexpected space ID ${space.id}`);
        }
        return [...spaces];
    }
    getSpaceByTileCard(cardName) {
        return this.spaces.find((space) => space.tile !== undefined && space.tile.card === cardName);
    }
    getOceansOnBoard(countUpgradedOceans = true) {
        return this.getOceansTiles(countUpgradedOceans).length;
    }
    getOceansTiles(countUpgradedOceans) {
        if (!countUpgradedOceans) {
            return this.spaces.filter((space) => space.tile !== undefined &&
                space.tile.tileType === TileType_1.TileType.OCEAN);
        }
        else {
            return this.spaces.filter((space) => Board.isOceanSpace(space));
        }
    }
    getSpaces(spaceType, _player) {
        return this.spaces.filter((space) => space.spaceType === spaceType);
    }
    getEmptySpaces() {
        return this.spaces.filter((space) => space.tile === undefined && space.bonus.includes(SpaceBonus_1.SpaceBonus.RESTRICTED) === false);
    }
    getSpaceCount(tileType, player) {
        return this.spaces.filter((space) => { var _a; return ((_a = space.tile) === null || _a === void 0 ? void 0 : _a.tileType) === tileType && space.player === player; }).length;
    }
    getSpacesWithSpecialTiles() {
        const specialTileTypes = [
            TileType_1.TileType.COMMERCIAL_DISTRICT,
            TileType_1.TileType.ECOLOGICAL_ZONE,
            TileType_1.TileType.INDUSTRIAL_CENTER,
            TileType_1.TileType.LAVA_FLOWS,
            TileType_1.TileType.MINING_AREA,
            TileType_1.TileType.MINING_RIGHTS,
            TileType_1.TileType.MOHOLE_AREA,
            TileType_1.TileType.NATURAL_PRESERVE,
            TileType_1.TileType.NUCLEAR_ZONE,
            TileType_1.TileType.RESTRICTED_AREA,
            TileType_1.TileType.DEIMOS_DOWN,
            TileType_1.TileType.GREAT_DAM,
            TileType_1.TileType.MAGNETIC_FIELD_GENERATORS,
            TileType_1.TileType.BIOFERTILIZER_FACILITY,
            TileType_1.TileType.METALLIC_ASTEROID,
            TileType_1.TileType.SOLAR_FARM,
            TileType_1.TileType.OCEAN_FARM,
            TileType_1.TileType.OCEAN_SANCTUARY,
            TileType_1.TileType.MINING_STEEL_BONUS,
            TileType_1.TileType.MINING_TITANIUM_BONUS,
            TileType_1.TileType.PERSEVERANCE_LANDING,
        ];
        return this.spaces.filter((space) => space.tile !== undefined && specialTileTypes.includes(space.tile.tileType));
    }
    getAvailableSpacesForCity(player, shouldApplyGordonEffect = true) {
        if (player.cardIsInEffect(CardName_1.CardName.GORDON) && shouldApplyGordonEffect)
            return this.getAvailableSpacesOnLand(player);
        return this.getAvailableSpacesOnLand(player).filter((space) => this.getAdjacentSpaces(space).some((adjacentSpace) => Board.isCitySpace(adjacentSpace)) === false);
    }
    getAvailableSpacesForGreenery(player) {
        if (player.cardIsInEffect(CardName_1.CardName.GORDON))
            return this.getAvailableSpacesOnLand(player);
        const spacesForGreenery = this.getAvailableSpacesOnLand(player)
            .filter((space) => this.getAdjacentSpaces(space).find((adj) => adj.tile !== undefined && adj.player === player && adj.tile.tileType !== TileType_1.TileType.OCEAN) !== undefined);
        if (spacesForGreenery.length > 0) {
            return spacesForGreenery;
        }
        return this.getAvailableSpacesOnLand(player);
    }
    getAvailableSpacesForOcean(player) {
        return this.getSpaces(SpaceType_1.SpaceType.OCEAN, player)
            .filter((space) => space.tile === undefined &&
            (space.player === undefined || space.player === player)).concat(this.getSpaces(SpaceType_1.SpaceType.LAND, player).filter((space) => space.tile === undefined && space.bonus.includes(SpaceBonus_1.SpaceBonus.COVE)));
    }
    getAvailableSpacesOnLand(player) {
        const landSpaces = this.getSpaces(SpaceType_1.SpaceType.LAND, player).filter((space) => {
            var _a;
            const hasPlayerMarker = space.player !== undefined;
            const safeForPlayer = !hasPlayerMarker || space.player === player;
            const playableSpace = space.tile === undefined || AresHandler_1.AresHandler.hasHazardTile(space);
            const blockedByDesperateMeasures = ((_a = space.tile) === null || _a === void 0 ? void 0 : _a.protectedHazard) === true;
            const isRestricted = space.bonus.includes(SpaceBonus_1.SpaceBonus.RESTRICTED);
            const hasNomads = space.hasNomads;
            return !isRestricted && safeForPlayer && playableSpace && !blockedByDesperateMeasures && !hasNomads;
        });
        return landSpaces;
    }
    getNthAvailableLandSpace(distance, direction, player = undefined, predicate = (_x) => true) {
        const spaces = this.spaces.filter((space) => {
            return this.canPlaceTile(space) && (space.player === undefined || space.player === player);
        }).filter(predicate);
        let idx = (direction === 1) ? distance : (spaces.length - (distance + 1));
        if (spaces.length === 0) {
            throw new Error('no spaces available');
        }
        while (idx < 0) {
            idx += spaces.length;
        }
        while (idx >= spaces.length) {
            idx -= spaces.length;
        }
        return spaces[idx];
    }
    getNonReservedLandSpaces() {
        return this.spaces.filter((space) => {
            return space.spaceType === SpaceType_1.SpaceType.LAND &&
                (space.tile === undefined || AresHandler_1.AresHandler.hasHazardTile(space)) &&
                space.player === undefined && space.hasNomads === false;
        });
    }
    canPlaceTile(space) {
        return space.tile === undefined && space.spaceType === SpaceType_1.SpaceType.LAND && space.bonus.includes(SpaceBonus_1.SpaceBonus.RESTRICTED) === false;
    }
    static isCitySpace(space) {
        const cityTileTypes = [TileType_1.TileType.CITY, TileType_1.TileType.CAPITAL, TileType_1.TileType.OCEAN_CITY, TileType_1.TileType.NEW_HOLLAND];
        return space.tile !== undefined && cityTileTypes.includes(space.tile.tileType);
    }
    static isGreenerySpace(space) {
        return space.tile !== undefined && space.tile.tileType === TileType_1.TileType.GREENERY;
    }
    static isOceanSpace(space) {
        const oceanTileTypes = [TileType_1.TileType.OCEAN, TileType_1.TileType.OCEAN_CITY, TileType_1.TileType.OCEAN_FARM, TileType_1.TileType.OCEAN_SANCTUARY, TileType_1.TileType.NEW_HOLLAND];
        return space.tile !== undefined && oceanTileTypes.includes(space.tile.tileType);
    }
    serialize() {
        return {
            spaces: this.spaces.map((space) => {
                var _a;
                return {
                    id: space.id,
                    spaceType: space.spaceType,
                    tile: space.tile,
                    player: (_a = space.player) === null || _a === void 0 ? void 0 : _a.id,
                    bonus: space.bonus,
                    adjacency: space.adjacency,
                    hasCathedral: space.hasCathedral === true,
                    hasNomads: space.hasNomads === true,
                    x: space.x,
                    y: space.y,
                };
            }),
        };
    }
    static deserializeSpace(serialized, players) {
        const playerId = serialized.player;
        const player = players.find((p) => p.id === playerId);
        const space = {
            id: serialized.id,
            spaceType: serialized.spaceType,
            bonus: serialized.bonus,
            hasCathedral: serialized.hasCathedral === true,
            hasNomads: serialized.hasNomads === true,
            x: serialized.x,
            y: serialized.y,
        };
        if (serialized.tile !== undefined) {
            space.tile = serialized.tile;
        }
        if (player !== undefined) {
            space.player = player;
        }
        if (serialized.adjacency !== undefined) {
            space.adjacency = serialized.adjacency;
        }
        return space;
    }
    static deserializeSpaces(spaces, players) {
        return spaces.map((space) => Board.deserializeSpace(space, players));
    }
}
exports.Board = Board;
//# sourceMappingURL=Board.js.map